<script setup>
import { ref } from 'vue';

const props = defineProps({
    showSenderName: {
        type: Boolean,
        default: false,
    },
    senderName: {
        type: String,
        default: '',
    },
    senderNamePlaceholder: {
        type: String,
        default: '',
    },
    partnerLogoUrl: {
        type: String,
        default: '',
    },
    highlightFreeProp: {
        type: Boolean,
        default: false,
    },
    defaultLanguage: {
        type: String,
        default: 'en',
    },
    languageOptions: {
        type: Array,
        default: () => [],
    },
});

const showSenderName = ref(props.showSenderName);
const senderName = ref(props.senderName);
const highlightFreeProp = ref(props.highlightFreeProp);
const defaultLanguage = ref(props.defaultLanguage);

const logoFileInput = ref(null);
const logoPreview = ref(props.partnerLogoUrl || '');
const newLogoSelected = ref(false);
const removeLogo = ref(false);

const onLogoChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
        return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
        logoPreview.value = e.target?.result || '';
    };
    reader.readAsDataURL(file);
    newLogoSelected.value = true;
    removeLogo.value = false;
};

const onRemoveLogo = () => {
    logoPreview.value = '';
    newLogoSelected.value = false;
    removeLogo.value = true;
    if (logoFileInput.value) {
        logoFileInput.value.value = '';
    }
};
</script>

<template>
    <div class="space-y-4">
        <div class="space-y-2">
            <div class="flex items-center space-x-2">
                <input
                    type="checkbox"
                    id="welcome_show_sender_name"
                    class="w-4 h-4 border-gray-300 rounded text-myflowGreen-600 focus:ring-myflowGreen-500"
                    v-model="showSenderName"
                />
                <label for="welcome_show_sender_name" class="text-sm font-medium text-gray-700">
                    Show sender name on the welcome page
                </label>
            </div>
            <input type="hidden" name="welcome_show_sender_name" :value="showSenderName ? '1' : '0'" />

            <div v-if="showSenderName" class="ml-6">
                <label for="welcome_sender_name" class="block text-sm text-gray-700">
                    Name to display
                </label>
                <input
                    type="text"
                    id="welcome_sender_name"
                    name="welcome_sender_name"
                    v-model="senderName"
                    :placeholder="senderNamePlaceholder"
                    maxlength="120"
                    class="myflow-basic-input"
                />
                <p class="text-xs text-gray-500 mt-1">
                    Shown as "Personally invited by {{ senderName || senderNamePlaceholder }}" above the headline.
                </p>
            </div>
        </div>

        <div class="space-y-2 pt-4 border-t">
            <label class="block text-sm font-medium text-gray-700">Partner logo</label>
            <p class="text-xs text-gray-500">
                Displayed side-by-side with the myflow logo at the top of the welcome page.
            </p>
            <div v-if="logoPreview" class="flex items-center space-x-4">
                <img :src="logoPreview" alt="Partner logo preview" class="h-12 object-contain bg-gray-50 border border-gray-200 rounded p-1" />
                <button
                    type="button"
                    class="text-xs text-red-600 mf-link"
                    @click="onRemoveLogo"
                >
                    Remove
                </button>
            </div>
            <input
                ref="logoFileInput"
                type="file"
                name="welcome_partner_logo"
                accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/gif"
                class="text-sm"
                @change="onLogoChange"
            />
            <input type="hidden" name="remove_partner_logo" :value="removeLogo ? '1' : '0'" />
        </div>

        <div class="space-y-2 pt-4 border-t">
            <div class="flex items-center space-x-2">
                <input
                    type="checkbox"
                    id="welcome_highlight_free_prop"
                    class="w-4 h-4 border-gray-300 rounded text-myflowGreen-600 focus:ring-myflowGreen-500"
                    v-model="highlightFreeProp"
                />
                <label for="welcome_highlight_free_prop" class="text-sm font-medium text-gray-700">
                    Highlight the "6 months free" offer
                </label>
            </div>
            <input type="hidden" name="welcome_highlight_free_prop" :value="highlightFreeProp ? '1' : '0'" />
            <p class="text-xs text-gray-500 ml-6">
                Adds a dedicated banner near the top of the welcome page.
            </p>
        </div>

        <div class="space-y-2 pt-4 border-t">
            <label for="welcome_default_language" class="block text-sm font-medium text-gray-700">
                Default language
            </label>
            <select
                id="welcome_default_language"
                name="welcome_default_language"
                v-model="defaultLanguage"
                class="myflow-basic-input"
            >
                <option v-for="opt in languageOptions" :key="opt.value" :value="opt.value">
                    {{ opt.label }}
                </option>
            </select>
            <p class="text-xs text-gray-500">
                The welcome page will render in this language by default.
            </p>
        </div>
    </div>
</template>
